import type { BrandTheme } from '@checkout/themes'

export const vigilkidsTheme: BrandTheme = {
  colors: {
    // Primary brand color - VigilKids green
    primary: '#24C790',
    primaryHover: '#1FB37F',
    primaryActive: '#00A66E',
    primaryAlpha: 'rgba(36, 199, 144, 0.1)',
    secondary: '#10B981',
    // Card/component background
    background: '#FFFFFF',
    // Page background (outside cards)
    pageBackground: '#F5F6FA',
    // Header background (50% transparent white)
    headerBackground: 'rgba(255, 255, 255, 0.5)',
    // Header text/icon color - dark for light header
    headerText: '#112043',
    // Surface for subtle backgrounds
    surface: '#F9FAFB',
    surfaceHover: '#F3F4F6',
    // Form section background (e.g., card payment form)
    formBackground: '#F8FAFB',
    // Default border
    border: '#EFEFEF',
    borderFocus: '#24C790',
    // Light border for cards
    borderLight: '#F3F4F7',
    // Divider color
    divider: '#E2ECE5',
    // Selected state border (blue for payment methods)
    selected: '#216CE0',
    text: {
      // Main heading text
      heading: '#21272F',
      // Primary body text
      primary: '#333333',
      // Secondary text
      secondary: '#666666',
      // Tertiary/muted text
      tertiary: '#999999',
      // Muted text for descriptions
      muted: '#999999',
      // Inverse text (on dark backgrounds)
      inverse: '#FFFFFF',
      // Link color
      link: '#24C790',
    },
    status: {
      error: '#EF4444',
      errorBackground: '#FEF2F2',
      success: '#24C790',
      successBackground: '#F0FDF4',
      warning: '#F59E0B',
      warningBackground: '#FFFBEB',
      info: '#3B82F6',
      infoBackground: '#EFF6FF',
    },
    price: {
      // Regular price
      regular: '#333333',
      // Total price (highlighted red)
      total: '#FF4A4A',
      // Original/strikethrough price
      original: '#666666',
    },
  },
  typography: {
    fontFamily: {
      base: '"PingFang SC", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      mono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    },
    fontSize: {
      xs: '0.75rem', // 12px
      sm: '0.875rem', // 14px
      base: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.75',
    },
  },
  spacing: {
    px: '1px',
    '0.5': '0.125rem',
    '1': '0.25rem',
    '2': '0.5rem',
    '3': '0.75rem',
    '4': '1rem',
    '5': '1.25rem',
    '6': '1.5rem',
    '8': '2rem',
    '10': '2.5rem',
    '12': '3rem',
    '16': '4rem',
  },
  borderRadius: {
    none: '0',
    sm: '0.25rem', // 4px
    md: '0.375rem', // 6px
    lg: '0.5rem', // 8px
    xl: '1rem', // 16px
    ml: '0.625rem', // 10px
    '2ml': '1.25rem', // 20px
    full: '9999px',
  },
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    // Header shadow from Figma
    md: '0 4px 10px 0 rgba(0, 56, 37, 0.06)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  },
  transitions: {
    fast: '150ms ease',
    normal: '200ms ease',
    slow: '300ms ease',
  },
  layout: {
    // Maximum content width (from Figma: 320px to 1600px = 1280px)
    maxWidth: '1280px',
    // Order summary sidebar width
    sidebarWidth: '546px',
    // Header height
    headerHeight: '70px',
    // Input field height
    inputHeight: '50px',
    // Button height (large)
    buttonHeight: '60px',
    // Payment method button height
    paymentMethodHeight: '42px',
    // Card padding
    cardPadding: '40px',
  },
}
